import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FaPlus, 
  FaStickyNote, 
  FaListUl, 
  FaThumbtack, 
  FaArchive, 
  FaTrash, 
  FaTimes,
  FaCheck,
  FaEllipsisV,
  FaPalette,
  FaEdit,
  FaSearch,
  FaInbox,
  FaUndo
} from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../utils/constants';
import session from '../utils/session';
import useConfirm from '../hooks/useConfirm';
import { useLanguage } from '../context/LanguageContext';
import showToast from '../utils/toast';
import offlineStorage from '../offline/OfflineStorage';
import onlineDetector from '../offline/OnlineDetector';
import syncEngine from '../offline/SyncEngine';
import './Note.scss';
import PageHero from '../components/PageHero/PageHero';

const COLORS = [
  { id: '#ffffff', name: 'Default', class: 'color-default' },
  { id: '#fff9c4', name: 'Yellow', class: 'color-yellow' },
  { id: '#f3e5f5', name: 'Purple', class: 'color-purple' },
  { id: '#e3f2fd', name: 'Blue', class: 'color-blue' },
  { id: '#e8f5e9', name: 'Green', class: 'color-green' },
  { id: '#fce4ec', name: 'Pink', class: 'color-pink' },
  { id: '#fff3e0', name: 'Orange', class: 'color-orange' },
  { id: '#e0f7fa', name: 'Teal', class: 'color-teal' }
];

// Pastel note colors are light and always use dark text — valid in both themes.
const DEFAULT_NOTE_COLOR = '#ffffff';

const getNoteColorVar = (color) => {
  // The default color adapts to the active theme via the surface token;
  // pastel colors are applied as-is and get a `.colored` modifier in markup.
  return color === DEFAULT_NOTE_COLOR ? 'var(--color-surface)' : color;
};

const isNoteColored = (color) => color !== DEFAULT_NOTE_COLOR;

const Note = () => {
  const { t } = useLanguage();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // New note state
  const [showNewNote, setShowNewNote] = useState(false);
  const [newNoteType, setNewNoteType] = useState('text');
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    checklist: [],
    color: '#ffffff'
  });
  const [newChecklistItem, setNewChecklistItem] = useState('');

  // Edit note state
  const [editingNote, setEditingNote] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editChecklistItem, setEditChecklistItem] = useState('');

  // UI state
  const [activeMenu, setActiveMenu] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(null);

  const confirm = useConfirm();
  const location = useLocation();
  const pendingNoteIdRef = useRef(null);
  const [highlightedNoteId, setHighlightedNoteId] = useState(null);

  useEffect(() => {
    if (location?.state?.openAdd) {
      setShowNewNote(true);
      try { window.history.replaceState({}, document.title); } catch (e) {}
    }
    // Deep-link for push clicks when app was closed (SW opens /notes?id=...)
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId) {
        const note = notes.find(n => String(n._id) === String(qId) || String(n.id) === String(qId));
        if (note) {
          setHighlightedNoteId(String(qId));
          openEditModal(note);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
          setTimeout(() => {
            const el = document.querySelector(`[data-note-id="${qId}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        } else {
          pendingNoteIdRef.current = String(qId);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry pending deep-link when notes load
  useEffect(() => {
    if (pendingNoteIdRef.current && notes.length) {
      const note = notes.find(n => String(n._id) === String(pendingNoteIdRef.current) || String(n.id) === String(pendingNoteIdRef.current));
      if (note) {
        setHighlightedNoteId(pendingNoteIdRef.current);
        openEditModal(note);
        pendingNoteIdRef.current = null;
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } catch (e) {}
      }
    }
  }, [notes]);

  // Handle push deep-link when app already open (SW client.navigate)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId) {
        const note = notes.find(n => String(n._id) === String(qId) || String(n.id) === String(qId));
        if (note) {
          setHighlightedNoteId(String(qId));
          openEditModal(note);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Also handle in-app bell-panel notifications
  useEffect(() => {
    const handler = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail || detail.path !== '/notes') return;
        const state = detail.state || {};
        if (state.openViewId) {
          const qId = String(state.openViewId);
          const note = notes.find(n => String(n._id) === qId || String(n.id) === qId);
          if (note) {
            setHighlightedNoteId(qId);
            openEditModal(note);
          } else pendingNoteIdRef.current = qId;
        }
      } catch (e) {}
    };
    window.addEventListener('ui:notification', handler);
    return () => window.removeEventListener('ui:notification', handler);
  }, [notes]);

  // Helper to convert offline record to note object
  const offlineRecordToNote = useCallback((record) => {
    const note = { ...record.data };
    note._id = note._id || record.serverId || record.localId;
    if (!note.updatedAt && record.updatedAt) note.updatedAt = record.updatedAt;
    return note;
  }, []);

  // Merge all local IndexedDB notes missing from the server response,
  // regardless of serverId (handles stale SW cache or server omissions)
  const ensureOfflineNotes = useCallback(async (currentNotes) => {
    try {
      const records = await offlineStorage.getAll('Note');
      if (records.length === 0) return currentNotes;
      const localNotes = records.map(offlineRecordToNote);
      const existingIds = new Set(currentNotes.map(n => n._id));
      const missing = localNotes.filter(n => !existingIds.has(n._id));
      if (missing.length === 0) return currentNotes;
      const merged = [...currentNotes, ...missing];
      merged.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
      });
      return merged;
    } catch {
      return currentNotes;
    }
  }, [offlineRecordToNote]);

  // Fetch notes — load from cache first for instant display, then refresh from server
  const fetchNotes = useCallback(async () => {
    // Always try loading from IndexedDB first for instant display
    let cachedNotes = [];
    try {
      const records = await offlineStorage.getAll('Note');
      cachedNotes = records.map(offlineRecordToNote).filter(n => {
        const archived = n.archived === undefined ? false : n.archived;
        return archived === showArchived;
      });
      cachedNotes.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
      });
    } catch (e) {
      console.error('Error loading from IndexedDB:', e);
    }

    // Show cached data immediately (no loading spinner)
    if (cachedNotes.length > 0) {
      setNotes(cachedNotes);
      setLoading(false);
    }

    // Then try fetching from server
    try {
      const token = session.getToken();
      const res = await fetch(`${API_BASE_URL}/notes?archived=${showArchived}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        offlineStorage.cacheServerData('Note', data).catch(() => {});
        const merged = await ensureOfflineNotes(data);
        // Only show notes matching the currently active tab. Locally cached
        // records can have a stale archived flag (e.g. empty server response,
        // offline full-cache fallback), so re-filter after merging.
        const tabNotes = merged.filter(n => {
          const archived = n.archived === undefined ? false : n.archived;
          return archived === showArchived;
        });
        setNotes(tabNotes);
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Error fetching notes from server:', error);
    }

    // If no cached data and server failed, show empty
    if (cachedNotes.length === 0) {
      setNotes([]);
    }
    setLoading(false);
  }, [showArchived, offlineRecordToNote, ensureOfflineNotes]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Listen for sync engine completion to refresh notes
  useEffect(() => {
    const unsub = syncEngine.onSync((event) => {
      if (event.type === 'complete') {
        fetchNotes();
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNotes]);

  // Filter notes by search term
  const filteredNotes = notes.filter(note => {
    const searchLower = searchTerm.toLowerCase();
    const matchesTitle = note.title?.toLowerCase().includes(searchLower);
    const matchesContent = note.content?.toLowerCase().includes(searchLower);
    const matchesChecklist = note.checklist?.some(item => 
      item.text?.toLowerCase().includes(searchLower)
    );
    return matchesTitle || matchesContent || matchesChecklist;
  });

  // Separate pinned and unpinned notes
  const pinnedNotes = filteredNotes.filter(n => n.pinned);
  const unpinnedNotes = filteredNotes.filter(n => !n.pinned);

  // Create new note
  const handleCreateNote = async () => {
    const finalChecklist = [...newNote.checklist];
    if (newNoteType === 'checklist' && newChecklistItem.trim()) {
      finalChecklist.push({ text: newChecklistItem.trim(), completed: false });
    }

    if (!newNote.title.trim() && !newNote.content.trim() && finalChecklist.length === 0) {
      setShowNewNote(false);
      return;
    }

    const noteData = {
      title: newNote.title,
      type: newNoteType,
      color: newNote.color,
      ...(newNoteType === 'text' 
        ? { content: newNote.content }
        : { checklist: finalChecklist }
      )
    };

    if (onlineDetector.isOnline) {
      try {
        const token = session.getToken();
        const res = await fetch(`${API_BASE_URL}/notes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(noteData),
        });
        if (res.ok) {
          fetchNotes();
          try { window.dispatchEvent(new CustomEvent('notifications:refresh')); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),600); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),1600);} catch(e){}
          resetNewNote();
          return;
        }
      } catch (error) {
        console.error('Error creating note:', error);
      }
    }

    // Offline save
    try {
      const localId = 'note_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      offlineStorage.save('Note', { ...noteData, archived: false, pinned: false, _id: localId, localId }).catch(() => {});
      const newNoteObj = { ...noteData, archived: false, pinned: false, _id: localId, id: localId, updatedAt: Date.now(), createdAt: Date.now() };
      setNotes(prev => [newNoteObj, ...prev]);
      showToast('Note saved offline. Will sync when online.', { type: 'info' });
    } catch (error) {
      console.error('Error saving offline:', error);
      showToast('Failed to save note offline.', { type: 'error' });
    }
    resetNewNote();
  };

  const resetNewNote = () => {
    setShowNewNote(false);
    setNewNoteType('text');
    setNewNote({
      title: '',
      content: '',
      checklist: [],
      color: '#ffffff'
    });
    setNewChecklistItem('');
  };

  // Add checklist item to new note
  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setNewNote(prev => ({
        ...prev,
        checklist: [...prev.checklist, { text: newChecklistItem.trim(), completed: false }]
      }));
      setNewChecklistItem('');
    }
  };

  // Remove checklist item from new note
  const removeChecklistItem = (index) => {
    setNewNote(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  // Toggle pin
  const handleTogglePin = async (noteId) => {
    if (!onlineDetector.isOnline) {
      showToast('Cannot update notes while offline', { type: 'error' });
      setActiveMenu(null);
      return;
    }

    try {
      const token = session.getToken();
      const res = await fetch(`${API_BASE_URL}/notes/${noteId}/pin`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) {
        fetchNotes();
        setActiveMenu(null);
        return;
      }
      showToast('Failed to update pin', { type: 'error' });
    } catch (error) {
      console.error('Error toggling pin:', error);
      showToast('Network error. Please try again.', { type: 'error' });
    }
    setActiveMenu(null);
  };

  // Toggle archive
  const handleToggleArchive = async (noteId) => {
    if (!onlineDetector.isOnline) {
      showToast('Cannot update notes while offline', { type: 'error' });
      setActiveMenu(null);
      return;
    }

    try {
      const token = session.getToken();
      const res = await fetch(`${API_BASE_URL}/notes/${noteId}/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) {
        fetchNotes();
        setActiveMenu(null);
        return;
      }
      showToast('Failed to update archive status', { type: 'error' });
    } catch (error) {
      console.error('Error toggling archive:', error);
      showToast('Network error. Please try again.', { type: 'error' });
    }
    setActiveMenu(null);
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    const ok = await confirm('Are you sure you want to delete this note?');
    if (!ok) return;

    if (!onlineDetector.isOnline) {
      showToast('Cannot delete notes while offline', { type: 'error' });
      setActiveMenu(null);
      return;
    }

    try {
      const token = session.getToken();
      const res = await fetch(`${API_BASE_URL}/notes/${noteId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok || res.status === 404) {
        offlineStorage.remove('Note', noteId, { skipQueue: true }).catch(() => {});
        fetchNotes();
        setActiveMenu(null);
        return;
      }
      showToast('Failed to delete note', { type: 'error' });
    } catch (error) {
      console.error('Error deleting note:', error);
      showToast('Network error. Please try again.', { type: 'error' });
    }
    setActiveMenu(null);
  };

  // Change note color
  const handleColorChange = async (noteId, color) => {
    if (!onlineDetector.isOnline) {
      showToast('Cannot update notes while offline', { type: 'error' });
      setShowColorPicker(null);
      return;
    }

    try {
      const token = session.getToken();
      const res = await fetch(`${API_BASE_URL}/notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ color }),
      });
      if (res.ok) {
        fetchNotes();
        setShowColorPicker(null);
        return;
      }
      showToast('Failed to update color', { type: 'error' });
    } catch (error) {
      console.error('Error changing color:', error);
      showToast('Network error. Please try again.', { type: 'error' });
    }
    setShowColorPicker(null);
  };

  // Open edit modal
  const openEditModal = (note) => {
    setEditingNote({ ...note });
    setShowEditModal(true);
    setActiveMenu(null);
  };

  // Save edited note
  const handleSaveEdit = async () => {
    if (!editingNote) return;

    if (!onlineDetector.isOnline) {
      showToast('Cannot edit notes while offline', { type: 'error' });
      setShowEditModal(false);
      setEditingNote(null);
      setEditChecklistItem('');
      return;
    }

    let finalChecklist = [...(editingNote.checklist || [])];
    if (editingNote.type === 'checklist' && editChecklistItem.trim()) {
      finalChecklist.push({ text: editChecklistItem.trim(), completed: false });
    }

    const updatedData = {
      title: editingNote.title,
      content: editingNote.content,
      checklist: finalChecklist,
      type: editingNote.type,
      color: editingNote.color
    };

    try {
      const token = session.getToken();
      const res = await fetch(`${API_BASE_URL}/notes/${editingNote._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(updatedData),
      });
      if (res.ok) {
        fetchNotes();
        showToast('Note updated successfully', { type: 'success' });
        setShowEditModal(false);
        setEditingNote(null);
        setEditChecklistItem('');
        return;
      }
      showToast('Failed to save note', { type: 'error' });
    } catch (error) {
      console.error('Error saving note:', error);
      showToast('Network error. Please try again.', { type: 'error' });
    }
    setShowEditModal(false);
    setEditingNote(null);
    setEditChecklistItem('');
  };

  // Toggle checklist item in edit mode
  const toggleEditChecklistItem = (index) => {
    setEditingNote(prev => ({
      ...prev,
      checklist: prev.checklist.map((item, i) =>
        i === index ? { ...item, completed: !item.completed } : item
      )
    }));
  };

  // Add checklist item in edit mode
  const addEditChecklistItem = () => {
    if (editChecklistItem.trim()) {
      setEditingNote(prev => ({
        ...prev,
        checklist: [...(prev.checklist || []), { text: editChecklistItem.trim(), completed: false }]
      }));
      setEditChecklistItem('');
    }
  };

  // Remove checklist item in edit mode
  const removeEditChecklistItem = (index) => {
    setEditingNote(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  // Toggle checklist item completion (inline)
  const handleToggleChecklistItem = async (noteId, itemId, currentCompleted) => {
    if (!onlineDetector.isOnline) {
      showToast('Cannot update notes while offline', { type: 'error' });
      return;
    }

    // Optimistically update local state without re-sorting (prevents card from moving)
    setNotes(prev => prev.map(note => {
      if (note._id !== noteId) return note;
      return {
        ...note,
        checklist: (note.checklist || []).map(item =>
          item._id === itemId ? { ...item, completed: !currentCompleted } : item
        )
      };
    }));

    try {
      const token = session.getToken();
      const res = await fetch(`${API_BASE_URL}/notes/${noteId}/checklist/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ completed: !currentCompleted }),
      });
      if (!res.ok) {
        showToast('Failed to update checklist', { type: 'error' });
        fetchNotes(); // Revert on failure
      }
    } catch (error) {
      console.error('Error toggling checklist item:', error);
      showToast('Network error. Please try again.', { type: 'error' });
      fetchNotes(); // Revert on error
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    
    // Include year if the note is from a different year
    const isSameYear = date.getFullYear() === now.getFullYear();
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Render note card
  const renderNoteCard = (note) => {
    // Sort checklist: unmarked items first, then marked items
    const sortedChecklist = [...(note.checklist || [])].sort((a, b) => {
      if (a.completed === b.completed) return 0;
      return a.completed ? 1 : -1;
    });
    const completedCount = sortedChecklist.filter(item => item.completed).length || 0;
    const totalCount = sortedChecklist.length || 0;

    const isHighlighted = highlightedNoteId && String(note._id) === String(highlightedNoteId);
    return (
      <div 
        key={note._id} 
        data-note-id={note._id}
        className={`note-card ${note.pinned ? 'note-card--pinned' : ''} ${isNoteColored(note.color) ? 'note-card--colored' : ''} ${isHighlighted ? 'note-card--highlight' : ''}`}
        style={{ backgroundColor: getNoteColorVar(note.color), ...(isHighlighted ? { boxShadow: '0 0 0 3px #6366f1, 0 8px 24px rgba(99,102,241,0.3)' } : {}) }}
        onClick={() => openEditModal(note)}
      >
        {note.pinned && (
          <div className="note-card__pin-indicator">
            <FaThumbtack />
          </div>
        )}

        {note.title && (
          <h3 className="note-card__title">{note.title}</h3>
        )}

        {note.type === 'text' ? (
          <p className="note-card__content">{note.content}</p>
        ) : (
          <div className="note-card__checklist">
            {sortedChecklist.slice(0, 5).map((item) => (
              <div 
                key={item._id} 
                className={`note-card__checklist-item ${item.completed ? 'note-card__checklist-item--completed' : ''}`}

              >
                <span className="note-card__checkbox">
                  {item.completed && <FaCheck />}
                </span>
                <span className="note-card__checklist-text">{item.text}</span>
              </div>
            ))}
            {totalCount > 5 && (
              <span className="note-card__more">+{totalCount - 5} more items</span>
            )}
            {totalCount > 0 && (
              <div className="note-card__progress">
                {completedCount}/{totalCount} completed
              </div>
            )}
          </div>
        )}

        <div className="note-card__footer">
          <span className="note-card__date">{formatDate(note.updatedAt)}</span>
          
          <div className="note-card__actions" onClick={(e) => e.stopPropagation()}>
            <button 
              className="note-card__action-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowColorPicker(showColorPicker === note._id ? null : note._id);
                setActiveMenu(null);
              }}
              title="Change color"
            >
              <FaPalette />
            </button>
            
            <button 
              className="note-card__action-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleTogglePin(note._id);
              }}
              title={note.pinned ? 'Unpin' : 'Pin'}
            >
              <FaThumbtack className={note.pinned ? 'pinned' : ''} />
            </button>
            
            <button 
              className="note-card__action-btn"
              onClick={(e) => {
                e.stopPropagation();
                setActiveMenu(activeMenu === note._id ? null : note._id);
                setShowColorPicker(null);
              }}
            >
              <FaEllipsisV />
            </button>

            {showColorPicker === note._id && (
              <div className="note-card__color-picker">
                {COLORS.map(color => (
                  <button
                    key={color.id}
                    className={`note-card__color-option ${note.color === color.id ? 'active' : ''}`}
                    style={{ backgroundColor: color.id }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleColorChange(note._id, color.id);
                    }}
                    title={color.name}
                  />
                ))}
              </div>
            )}

            {activeMenu === note._id && (
              <div className="note-card__menu">
                <button onClick={(e) => { e.stopPropagation(); openEditModal(note); }}>
                  <FaEdit /> {t('note.edit')}
                </button>
                <button onClick={(e) => { e.stopPropagation(); handleToggleArchive(note._id); }}>
                  {showArchived ? <FaUndo /> : <FaArchive />}
                  {showArchived ? t('note.unarchive') : t('note.archive')}
                </button>
                <button 
                  className="danger" 
                  onClick={(e) => { e.stopPropagation(); handleDeleteNote(note._id); }}
                >
                  <FaTrash /> {t('note.delete')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="notes-page">
      {/* Header */}
      <PageHero
        icon={<FaStickyNote />}
        title={t('note.title')}
        subtitle={t('note.subtitle')}
        actions={
          <button className="page-hero__btn" onClick={() => setShowNewNote(true)}>
            <FaPlus /> {t('note.addNote')}
          </button>
        }
      />

      {/* Search Bar + Tabs */}
      <div className="notes-page__toolbar">
        <div className="notes-page__search">
          <FaSearch className="notes-page__search-icon" />
          <input
            type="text"
            placeholder={t('note.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="notes-page__search-input"
          />
          {searchTerm && (
            <button 
              className="notes-page__search-clear"
              onClick={() => setSearchTerm('')}
            >
              <FaTimes />
            </button>
          )}
        </div>

        <div className="notes-page__tabs">
          <button
            className={`notes-page__tab ${!showArchived ? 'active' : ''}`}
            onClick={() => setShowArchived(false)}
          >
            <FaStickyNote /> {t('note.tabNotes')}
          </button>
          <button
            className={`notes-page__tab ${showArchived ? 'active' : ''}`}
            onClick={() => setShowArchived(true)}
          >
            <FaArchive /> {t('note.tabArchived')}
          </button>
        </div>
      </div>

      {/* Notes Grid */}
      {loading ? (
        <div className="notes-page__loading">
          <div className="notes-page__spinner" />
          <p>{t('note.loading')}</p>
        </div>
      ) : filteredNotes.length === 0 ? (
        <div className="notes-page__empty">
          <div className="notes-page__empty-icon">
            {showArchived ? <FaArchive /> : <FaInbox />}
          </div>
          <h3>{showArchived ? t('note.noArchived') : t('note.noNotes')}</h3>
          <p>{showArchived ? t('note.archivedEmpty') : t('note.notesEmpty')}</p>
        </div>
      ) : (
        <div className="notes-page__content">
          {pinnedNotes.length > 0 && !showArchived && (
            <div className="notes-section">
              <h2 className="notes-section__title">
                <FaThumbtack /> {t('note.pinned')}
              </h2>
              <div className="notes-grid">
                {pinnedNotes.map(renderNoteCard)}
              </div>
            </div>
          )}

          {unpinnedNotes.length > 0 && (
            <div className="notes-section">
              {pinnedNotes.length > 0 && !showArchived && (
                <h2 className="notes-section__title">{t('note.others')}</h2>
              )}
              <div className="notes-grid">
                {unpinnedNotes.map(renderNoteCard)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Note Modal */}
      {showNewNote && (
        <div className="note-modal-overlay" onClick={resetNewNote}>
          <div 
            className={`note-modal ${isNoteColored(newNote.color) ? 'note-modal--colored' : ''}`}
            style={{ backgroundColor: getNoteColorVar(newNote.color) }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="note-modal__header">
              <input
                type="text"
                placeholder={t('note.titlePlaceholder')}
                value={newNote.title}
                onChange={(e) => setNewNote(prev => ({ ...prev, title: e.target.value }))}
                className="note-modal__title-input"
                autoFocus
              />
              <button 
                className="note-modal__close"
                onClick={resetNewNote}
              >
                <FaTimes />
              </button>
            </div>

            <div className="note-modal__body">
              {/* Note Type Tabs */}
              <div className="new-note__type-tabs">
                <button 
                  className={`new-note__type-tab ${newNoteType === 'text' ? 'active' : ''}`}
                  onClick={() => setNewNoteType('text')}
                >
                  <FaStickyNote /> {t('note.typeText')}
                </button>
                <button 
                  className={`new-note__type-tab ${newNoteType === 'checklist' ? 'active' : ''}`}
                  onClick={() => setNewNoteType('checklist')}
                >
                  <FaListUl /> {t('note.typeChecklist')}
                </button>
              </div>

              {newNoteType === 'text' ? (
                <textarea
                  placeholder={t('note.noteContent')}
                  value={newNote.content}
                  onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                  className="note-modal__content-input"
                  rows={8}
                />
              ) : (
                <div className="new-note__checklist">
                  {newNote.checklist.map((item, index) => (
                    <div key={index} className="new-note__checklist-item">
                      <span className="new-note__checkbox" />
                      <span className="new-note__checklist-text">{item.text}</span>
                      <button 
                        className="new-note__checklist-remove"
                        onClick={() => removeChecklistItem(index)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <div className="new-note__checklist-input">
                    <span className="new-note__checkbox" />
                    <input
                      type="text"
                      placeholder={t('note.addItem')}
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
                    />
                    <button 
                      className="new-note__checklist-add"
                      onClick={addChecklistItem}
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Color Picker */}
            <div className="note-modal__colors">
              {COLORS.map(color => (
                <button
                  key={color.id}
                  className={`note-modal__color-btn ${newNote.color === color.id ? 'active' : ''}`}
                  style={{ backgroundColor: color.id }}
                  onClick={() => setNewNote(prev => ({ ...prev, color: color.id }))}
                  title={color.name}
                />
              ))}
            </div>

            <div className="note-modal__footer">
              <div className="note-modal__actions note-modal__actions--left">
                <button 
                  className="note-modal__cancel"
                  onClick={resetNewNote}
                >
                  {t('common.cancel')}
                </button>
              </div>
              <span className="note-modal__date note-modal__date--center"></span>
              <div className="note-modal__actions note-modal__actions--right">
                <button 
                  className="note-modal__save"
                  onClick={handleCreateNote}
                >
                  {t('note.saveNote')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingNote && (
        <div className="note-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div 
            className={`note-modal ${isNoteColored(editingNote.color) ? 'note-modal--colored' : ''}`}
            style={{ backgroundColor: getNoteColorVar(editingNote.color) }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="note-modal__header">
              <input
                type="text"
                placeholder={t('note.titlePlaceholder')}
                value={editingNote.title}
                onChange={(e) => setEditingNote(prev => ({ ...prev, title: e.target.value }))}
                className="note-modal__title-input"
              />
              <button 
                className="note-modal__close"
                onClick={() => setShowEditModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="note-modal__body">
              {editingNote.type === 'text' ? (
                <textarea
                  placeholder={t('note.noteContent')}
                  value={editingNote.content}
                  onChange={(e) => setEditingNote(prev => ({ ...prev, content: e.target.value }))}
                  className="note-modal__content-input"
                  rows={8}
                />
              ) : (
                <div className="note-modal__checklist">
                  {editingNote.checklist?.map((item, index) => (
                    <div 
                      key={item._id || index} 
                      className={`note-modal__checklist-item ${item.completed ? 'completed' : ''}`}
                    >
                      <button 
                        className="note-modal__checkbox"
                        onClick={() => toggleEditChecklistItem(index)}
                      >
                        {item.completed && <FaCheck />}
                      </button>
                      <span className="note-modal__checklist-text">{item.text}</span>
                      <button 
                        className="note-modal__checklist-remove"
                        onClick={() => removeEditChecklistItem(index)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <div className="note-modal__checklist-input">
                    <span className="note-modal__checkbox-placeholder" />
                    <input
                      type="text"
                      placeholder={t('note.addItem')}
                      value={editChecklistItem}
                      onChange={(e) => setEditChecklistItem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addEditChecklistItem()}
                    />
                    <button 
                      className="note-modal__checklist-add"
                      onClick={addEditChecklistItem}
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Color Picker */}
            <div className="note-modal__colors">
              {COLORS.map(color => (
                <button
                  key={color.id}
                  className={`note-modal__color-btn ${editingNote.color === color.id ? 'active' : ''}`}
                  style={{ backgroundColor: color.id }}
                  onClick={() => setEditingNote(prev => ({ ...prev, color: color.id }))}
                  title={color.name}
                />
              ))}
            </div>

            <div className="note-modal__footer">
              <div className="note-modal__actions note-modal__actions--left">
                <button 
                  className="note-modal__cancel"
                  onClick={() => setShowEditModal(false)}
                >
                  {t('common.cancel')}
                </button>
              </div>
              <span className="note-modal__date">
                Edited {formatDate(editingNote.updatedAt)}
              </span>
              <div className="note-modal__actions note-modal__actions--right">
                <button 
                  className="note-modal__save"
                  onClick={handleSaveEdit}
                >
                  {t('note.saveChanges')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menus */}
      {(activeMenu || showColorPicker) && (
        <div 
          className="notes-page__overlay"
          onClick={() => { setActiveMenu(null); setShowColorPicker(null); }}
        />
      )}
    </div>
  );
};

export default Note;
